import React, { useState, createContext, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import Layout from './components/Layout';
import IdeaOfTheDay from './pages/IdeaOfTheDay';
import IdeasVault from './pages/IdeasVault';
import BuildersStudio from './pages/BuildersStudio';
import IdeaDetail from './pages/IdeaDetail';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Auth from './pages/Auth';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const AppContent = () => {
  const { isAuthenticated } = useUser();

  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      
      {isAuthenticated ? (
        <>
          <Route path="/" element={<Layout><IdeaOfTheDay /></Layout>} />
          <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
          <Route path="/ideas-vault" element={<Layout><IdeasVault /></Layout>} />
          <Route path="/builders-studio" element={<Layout><BuildersStudio /></Layout>} />
          <Route path="/profile" element={<Layout><Profile /></Layout>} />
          <Route path="/idea/:id" element={<Layout><IdeaDetail /></Layout>} />
        </>
      ) : (
        <Route path="/*" element={<Auth />} />
      )}
    </Routes>
  );
};

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      return newMode;
    });
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Set initial direction based on language
  useEffect(() => {
    const initializeDirection = () => {
      const language = localStorage.getItem('i18nextLng') || 'en';
      document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', language);
    };
    
    initializeDirection();
  }, []);

  return (
    <UserProvider>
      <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
        <Router>
          <AppContent />
        </Router>
      </ThemeContext.Provider>
    </UserProvider>
  );
}

export default App;